import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Phone } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { trackEvent } from "../lib/analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function newSessionId() {
  let sid = localStorage.getItem("rihaai_chat_sid");
  if (!sid) {
    sid = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("rihaai_chat_sid", sid);
  }
  return sid;
}

export default function Chatbot() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(newSessionId());

  useEffect(() => {
    setMessages([{ role: "assistant", content: pick(T.chat.welcome, lang) }]);
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    trackEvent("chatbot_message_sent", { language: lang, message_length: msg.length });
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const resp = await fetch(`${BACKEND}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: msg, language: lang }),
      });
      if (!resp.body) throw new Error("no body");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: lang === "hi" ? "क्षमा करें, अभी उत्तर देने में समस्या है। कृपया लीड फॉर्म भरें।" : "Sorry, I'm having trouble responding right now. Please submit the lead form and we'll call you.",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <a
        href={`tel:${T.phone.number}`}
        className="hidden lg:flex fixed bottom-6 right-24 z-40 h-14 pl-4 pr-5 rounded-full bg-[#2C4C3B] text-white shadow-xl items-center gap-2 hover:scale-105 transition-transform"
        data-testid="fab-call"
        aria-label="Call Rihai"
      >
        <Phone size={18} strokeWidth={2.2} />
        <span className="text-sm font-medium hidden sm:inline">{pick(T.hero.ctaCall, lang)}</span>
      </a>

      <button
        onClick={() => {
          setOpen((o) => {
            if (!o) trackEvent("chatbot_opened", { language: lang });
            return !o;
          });
        }}
        className="fixed bottom-24 lg:bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#E2762A] text-white shadow-xl flex items-center justify-center hover:scale-110 transition-transform"
        data-testid="chatbot-toggle"
        aria-label="Open chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-40 w-[calc(100vw-3rem)] sm:w-[380px] max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-[#D8CDBA] flex flex-col overflow-hidden"
          data-testid="chatbot-panel"
        >
          <div className="bg-[#2C4C3B] text-white p-4">
            <div className="font-serif-display text-2xl leading-tight">{pick(T.chat.title, lang)}</div>
            <div className="text-xs text-[#D8CDBA] mt-1">{pick(T.chat.subtitle, lang)}</div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF9F6]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "msg-bubble-user" : "msg-bubble-bot"
                  }`}
                  data-testid={`chat-msg-${m.role}-${i}`}
                >
                  {m.content || (m.role === "assistant" && sending ? "…" : "")}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-[#D8CDBA] bg-white flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={pick(T.chat.placeholder, lang)}
              className="flex-1 px-4 py-2 rounded-full border border-[#D8CDBA] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] text-sm bg-[#FAF9F6]"
              data-testid="chat-input"
              disabled={sending}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#2C4C3B] text-white flex items-center justify-center disabled:opacity-40"
              data-testid="chat-send"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
